/**
 * ActivitiesDefStorage — manages local storage of ActivityDef content records.
 *
 * ActivityDef content arrives as a ContentType='ActivityDef' record from the backend.
 * The content_body column contains base64-encoded JSON that matches the C# ActivitiesDefDto
 * structure (PascalCase on the wire).
 *
 * Multiple ActivityDefs may be stored (e.g. one for Standard accounts, one for Premium),
 * each identified by its SourceId.
 *
 * Storage structure:
 *   Key:   activities_def_{sourceId}   e.g. activities_def_standard_en
 *   Value: JSON-serialised ActivitiesDefDto (camelCase)
 *   Index: activities_def_index        array of stored sourceIds
 *
 * Wire format (base64 → JSON, PascalCase):
 * {
 *   "SourceId": "standard_en",
 *   "Name": "Standard Activities",
 *   "Desc": "...",
 *   "LanguageTypeId": "1",
 *   "Domains": [
 *     {
 *       "Name": "Communication",
 *       "Desc": "...",
 *       "Groups": [
 *         {
 *           "Name": "Early Sounds",
 *           "Desc": "...",
 *           "Days": "90",
 *           "Items": [
 *             { "Name": "...", "Desc": "...", "DevDomain": "communication", "Days": "90" }
 *           ]
 *         }
 *       ]
 *     }
 *   ]
 * }
 */

import type { ContentRecord } from '../stores/contentStore';

// Re-use the same StorageInterface shape used throughout the codebase
export interface StorageInterface {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  removeItem(key: string): Promise<void>;
}

// ─── DTO interfaces (camelCase, mirrors C# PascalCase) ───────────────────────

export interface ActivitiesItemDto {
  /** Milestone variable name */
  mVar: string;
  /** Display name of the activity */
  name: string;
  /** Description / instructions */
  desc: string;
  /** Developmental domain key (e.g. "communication", "motor") */
  devDomain: string;
  /** Minimum age in days from birth at which this item is appropriate */
  days: string;
}

export interface ActivitiesGroupDto {
  /** Display name of the group */
  name: string;
  /** Optional group description */
  desc: string;
  /** Minimum age in days from birth for this group */
  days: string;
  items: ActivitiesItemDto[];
}

export interface ActivitiesDomainDto {
  /** Domain display name — becomes a tab in ActivitiesView */
  name: string;
  /** Domain description shown in the tab header */
  desc: string;
  /** Footer text shown at the bottom of the domain content */
  footer: string;
  groups: ActivitiesGroupDto[];
}

export interface ActivitiesDefDto {
  /** Unique identifier, e.g. "standard_en", "premium_en" */
  sourceId: string;
  /** Human-readable name, e.g. "Standard Activities" */
  name: string;
  /** Short description */
  desc: string;
  languageTypeId: string;
  domains: ActivitiesDomainDto[];
  /** ISO timestamp set when the record was stored locally */
  downloadedAt: string;
}

// ─── Storage class ────────────────────────────────────────────────────────────

export class ActivitiesDefStorage {
  private readonly keyPrefix = 'activities_def_';
  private readonly indexKey  = 'activities_def_index';

  constructor(private storage: StorageInterface) {}

  // ─── Private helpers ────────────────────────────────────────────────────────

  private storageKey(sourceId: string): string {
    return `${this.keyPrefix}${sourceId}`;
  }

  private decodeBase64(b64: string): string {
    try {
      const bufferApi = (globalThis as any).Buffer;
      if (bufferApi?.from) {
        return bufferApi.from(b64, 'base64').toString('utf-8');
      }
      return atob(b64);
    } catch (err) {
      throw new Error(`ActivitiesDefStorage: failed to decode base64 — ${err}`);
    }
  }

  private async addToIndex(sourceId: string): Promise<void> {
    try {
      const raw = await this.storage.getItem(this.indexKey);
      const ids: string[] = raw ? JSON.parse(raw) : [];
      if (!ids.includes(sourceId)) {
        ids.push(sourceId);
        await this.storage.setItem(this.indexKey, JSON.stringify(ids));
      }
    } catch (_) { /* non-fatal */ }
  }

  private async removeFromIndex(sourceId: string): Promise<void> {
    try {
      const raw = await this.storage.getItem(this.indexKey);
      const ids: string[] = raw ? JSON.parse(raw) : [];
      const updated = ids.filter(id => id !== sourceId);
      await this.storage.setItem(this.indexKey, JSON.stringify(updated));
    } catch (_) { /* non-fatal */ }
  }

  private async getIndex(): Promise<string[]> {
    try {
      const raw = await this.storage.getItem(this.indexKey);
      return raw ? JSON.parse(raw) : [];
    } catch (_) {
      return [];
    }
  }

  // ─── Public API ──────────────────────────────────────────────────────────────

  /**
   * Persist an ActivitiesDefDto keyed by sourceId.
   */
  async saveActivitiesDef(dto: ActivitiesDefDto): Promise<boolean> {
    try {
      const record: ActivitiesDefDto = { ...dto, downloadedAt: dto.downloadedAt || new Date().toISOString() };
      const sourceId = record.sourceId || 'unknown';
      await this.storage.setItem(this.storageKey(sourceId), JSON.stringify(record));
      await this.addToIndex(sourceId);
      console.log(`✅ ActivitiesDefStorage: saved ActivitiesDef sourceId="${sourceId}" (${record.domains.length} domain(s))`);
      return true;
    } catch (err) {
      console.error('❌ ActivitiesDefStorage.saveActivitiesDef failed:', err);
      return false;
    }
  }

  /**
   * Retrieve a stored ActivitiesDefDto by sourceId.
   * Falls back to the first available record when sourceId is omitted.
   */
  async getActivitiesDef(sourceId?: string): Promise<ActivitiesDefDto | null> {
    try {
      if (sourceId) {
        const raw = await this.storage.getItem(this.storageKey(sourceId));
        return raw ? (JSON.parse(raw) as ActivitiesDefDto) : null;
      }
      // Fallback: return first available
      const ids = await this.getIndex();
      if (ids.length === 0) return null;
      const raw = await this.storage.getItem(this.storageKey(ids[0]));
      return raw ? (JSON.parse(raw) as ActivitiesDefDto) : null;
    } catch (err) {
      console.error('❌ ActivitiesDefStorage.getActivitiesDef failed:', sourceId, err);
      return null;
    }
  }

  /**
   * Return all stored ActivitiesDefDto records.
   */
  async getAllActivitiesDefs(): Promise<ActivitiesDefDto[]> {
    const ids = await this.getIndex();
    const results: ActivitiesDefDto[] = [];
    for (const id of ids) {
      const dto = await this.getActivitiesDef(id);
      if (dto) results.push(dto);
    }
    return results;
  }

  /**
   * Delete an ActivitiesDefDto by sourceId.
   */
  async deleteActivitiesDef(sourceId: string): Promise<boolean> {
    try {
      await this.storage.removeItem(this.storageKey(sourceId));
      await this.removeFromIndex(sourceId);
      console.log(`🗑️ ActivitiesDefStorage: deleted sourceId="${sourceId}"`);
      return true;
    } catch (err) {
      console.error('❌ ActivitiesDefStorage.deleteActivitiesDef failed:', sourceId, err);
      return false;
    }
  }

  /**
   * Remove all stored ActivitiesDef records and the index.
   */
  async clearAll(): Promise<void> {
    const ids = await this.getIndex();
    for (const id of ids) {
      try { await this.storage.removeItem(this.storageKey(id)); } catch (_) { /* continue */ }
    }
    try { await this.storage.removeItem(this.indexKey); } catch (_) { /* non-fatal */ }
    console.log('🗑️ ActivitiesDefStorage: cleared all records');
  }

  // ─── Query helpers ────────────────────────────────────────────────────────────

  /**
   * Return all items from across all domains in a given def that are
   * appropriate at or before the child's age in days.
   * @param ageInDays  Child's chronological age in days
   * @param sourceId   Optional — defaults to first available def
   */
  async getItemsForAge(ageInDays: number, sourceId?: string): Promise<ActivitiesItemDto[]> {
    const dto = await this.getActivitiesDef(sourceId);
    if (!dto) return [];
    const results: ActivitiesItemDto[] = [];
    for (const domain of dto.domains) {
      for (const group of domain.groups) {
        const groupDays = parseInt(group.days, 10);
        if (!isNaN(groupDays) && groupDays > ageInDays) continue;
        for (const item of group.items) {
          const itemDays = parseInt(item.days, 10);
          if (!isNaN(itemDays) && itemDays > ageInDays) continue;
          results.push(item);
        }
      }
    }
    return results;
  }

  /**
   * Return all items that belong to a specific developmental domain key
   * (case-insensitive match on `devDomain`).
   * @param devDomain  e.g. "communication", "motor"
   * @param sourceId   Optional — defaults to first available def
   */
  async getItemsForDevDomain(devDomain: string, sourceId?: string): Promise<ActivitiesItemDto[]> {
    const dto = await this.getActivitiesDef(sourceId);
    if (!dto) return [];
    const lower = devDomain.toLowerCase();
    const results: ActivitiesItemDto[] = [];
    for (const domain of dto.domains) {
      for (const group of domain.groups) {
        for (const item of group.items) {
          if (item.devDomain.toLowerCase() === lower) results.push(item);
        }
      }
    }
    return results;
  }

  // ─── Content-record unpacking ─────────────────────────────────────────────────

  /**
   * Unpack a ContentStore record of type "ActivityDef" and persist it.
   *
   * Expected record.tableData shape (standard content-server response):
   *   Columns: [..., "content_body", ...]
   *   Rows:    [[..., "<base64-encoded JSON>", ...]]
   *
   * The base64-encoded JSON payload must match the C# ActivitiesDefDto structure.
   * Returns the stored dto on success, null on failure.
   */
  async unpackFromContentRecord(record: ContentRecord): Promise<ActivitiesDefDto | null> {
    try {
      console.log('\n🔍 ActivitiesDefStorage.unpackFromContentRecord called for:', record.id);

      const tableData = (record as any).tableData;
      if (!tableData?.Data?.Columns || !tableData?.Data?.Rows?.length) {
        console.warn('⚠️ ActivitiesDefStorage: ContentRecord has no tableData rows');
        return null;
      }

      const columns: string[] = tableData.Data.Columns;
      const bodyIdx = columns.findIndex((c: string) => c.toLowerCase() === 'content_body');
      if (bodyIdx === -1) {
        console.warn('⚠️ ActivitiesDefStorage: content_body column not found. Available:', columns);
        return null;
      }

      const firstRow = tableData.Data.Rows[0];
      const encodedBody: string = firstRow[bodyIdx];
      if (!encodedBody) {
        console.warn('⚠️ ActivitiesDefStorage: content_body is empty');
        return null;
      }

      const jsonString = this.decodeBase64(encodedBody);
      const p = JSON.parse(jsonString); // p = parsed PascalCase object from C# backend

      // ── Map PascalCase → camelCase ──────────────────────────────────────────

      const mapItems = (arr: any[]): ActivitiesItemDto[] =>
        (arr ?? []).map((i: any): ActivitiesItemDto => ({
          mVar:      i.MVar      ?? i.mVar      ?? '',
          name:      i.Name      ?? i.name      ?? '',
          desc:      i.Desc      ?? i.desc      ?? '',
          devDomain: i.DevDomain ?? i.devDomain ?? '',
          days:      i.Days      ?? i.days      ?? '0',
        }));

      const mapGroups = (arr: any[]): ActivitiesGroupDto[] =>
        (arr ?? []).map((g: any): ActivitiesGroupDto => ({
          name:  g.Name  ?? g.name  ?? '',
          desc:  g.Desc  ?? g.desc  ?? '',
          days:  g.Days  ?? g.days  ?? '0',
          items: mapItems(g.Items ?? g.items ?? []),
        }));

      const domains: ActivitiesDomainDto[] = (p.Domains ?? p.domains ?? []).map((d: any): ActivitiesDomainDto => ({
        name:   d.Name   ?? d.name   ?? '',
        desc:   d.Desc   ?? d.desc   ?? '',
        footer: d.Footer ?? d.footer ?? '',
        groups: mapGroups(d.Groups ?? d.groups ?? []),
      }));

      const dto: ActivitiesDefDto = {
        sourceId:      p.SourceId      ?? p.sourceId      ?? 'unknown',
        name:          p.Name          ?? p.name          ?? '',
        desc:          p.Desc          ?? p.desc          ?? '',
        languageTypeId: p.LanguageTypeId ?? p.languageTypeId ?? '1',
        domains,
        downloadedAt:  new Date().toISOString(),
      };

      const totalItems = domains.reduce((sum, d) =>
        sum + d.groups.reduce((gs, g) => gs + g.items.length, 0), 0);

      console.log(`📋 ActivitiesDefStorage: parsed ${dto.domains.length} domain(s), ${totalItems} item(s) for sourceId="${dto.sourceId}"`);

      const saved = await this.saveActivitiesDef(dto);
      if (!saved) return null;

      console.log(`✅ ActivitiesDefStorage: unpacked & stored ActivitiesDef (sourceId="${dto.sourceId}", name="${dto.name}")`);
      return dto;
    } catch (err) {
      console.error('❌ ActivitiesDefStorage.unpackFromContentRecord failed:', err);
      return null;
    }
  }
}
